import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LifeBuoy, Timer, BookOpen, Gamepad2, Sparkles, Heart, Ear, Shield, ChevronRight, RefreshCw } from "lucide-react";
import EncouragementTicker from "@/components/EncouragementTicker";
import CommunitySection from "@/components/CommunitySection";
import PublicStatsWidget from "@/components/PublicStatsWidget";
import { api } from "@/lib/api";

const SHORTCUTS = [
  {
    to: "/sos",
    Icon: LifeBuoy,
    tone: "orange",
    title: "Urgence & SOS",
    desc: "Numéros gratuits 24/7 et formulaire d'écoute confidentiel.",
    cta: "Avoir de l'aide",
  },
  {
    to: "/guides",
    Icon: BookOpen,
    tone: "sky",
    title: "Guides de crise",
    desc: "Que faire quand l'envie est trop forte, soigner, parler.",
    cta: "Voir les guides",
  },
  {
    to: "/compteur",
    Icon: Timer,
    tone: "emerald",
    title: "Compteur & outils",
    desc: "Ton compteur personnel, tes badges, ton journal.",
    cta: "Ouvrir mon espace",
  },
  {
    to: "/passions",
    Icon: Gamepad2,
    tone: "violet",
    title: "Passions & gaming",
    desc: "Moto, jeux vidéo, sessions à plusieurs, Discord.",
    cta: "Rejoindre",
  },
];

const TONES = {
  orange: { bg: "rgba(249, 115, 22, 0.12)", border: "rgba(249, 115, 22, 0.35)", accent: "#F97316" },
  sky: { bg: "rgba(56, 189, 248, 0.12)", border: "rgba(56, 189, 248, 0.35)", accent: "#38BDF8" },
  emerald: { bg: "rgba(16, 185, 129, 0.12)", border: "rgba(16, 185, 129, 0.35)", accent: "#10B981" },
  violet: { bg: "rgba(167, 139, 250, 0.12)", border: "rgba(167, 139, 250, 0.35)", accent: "#A78BFA" },
};

export default function Home() {
  const [quote, setQuote] = useState("Un pas aujourd'hui, c'est 365 pas dans un an.");
  const [loading, setLoading] = useState(false);

  const refreshQuote = async () => {
    try {
      setLoading(true);
      const r = await api.get("/motivation");
      setQuote(r.data.quote);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { refreshQuote(); }, []);

  return (
    <>
      <EncouragementTicker />

      <section className="relative py-14 lg:py-20 overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1476362555312-ab9e108a0b7e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHw0fHxjYWxtJTIwZGFyayUyMG5hdHVyZSUyMG1pc3R8ZW58MHx8fHwxNzkxOTUxMDY3fDA&ixlib=rb-4.1.0&q=85)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            maskImage: "linear-gradient(180deg, rgba(0,0,0,1) 0%, rgba(0,0,0,0.5) 60%, transparent 100%)",
            WebkitMaskImage: "linear-gradient(180deg, rgba(0,0,0,1) 0%, rgba(0,0,0,0.5) 60%, transparent 100%)",
          }}
        />
        <div className="relative max-w-6xl mx-auto px-5 lg:px-8">
          <motion.span initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="em-eyebrow">
            Écoute · Soutien · Bienveillance
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
            className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white max-w-3xl"
          >
            Une équipe pour ton <span className="text-orange-400">mental</span>, tes <span className="text-emerald-400">passions</span> et tes projets.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.12 }}
            className="mt-5 text-base lg:text-lg leading-relaxed text-slate-300 max-w-2xl"
          >
            Un espace sûr pour parler sans jugement, respirer, suivre ta progression et traverser
            les moments difficiles. Choisis ci-dessous ce dont tu as besoin maintenant.
          </motion.p>
          <div className="mt-7 flex flex-wrap gap-2">
            {[{ Icon: Ear, label: "On t'écoute" }, { Icon: Shield, label: "Sans jugement" }, { Icon: Heart, label: "Tu comptes" }].map(({ Icon, label }) => (
              <span key={label} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-sm text-slate-200">
                <Icon className="w-3.5 h-3.5 text-orange-300" /> {label}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Shortcuts */}
      <section className="pb-16 lg:pb-20">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <span className="em-eyebrow">Raccourcis</span>
          <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
            Que cherches-tu, maintenant ?
          </h2>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {SHORTCUTS.map((s) => {
              const t = TONES[s.tone];
              return (
                <Link
                  to={s.to}
                  key={s.to}
                  data-testid={`shortcut-${s.to.slice(1)}`}
                  className="group rounded-2xl border p-6 transition hover:-translate-y-1 hover:shadow-xl"
                  style={{ background: `linear-gradient(180deg, ${t.bg}, rgba(15, 23, 42, 0.9))`, borderColor: t.border }}
                >
                  <div className="w-11 h-11 rounded-xl grid place-items-center border" style={{ background: `${t.accent}25`, borderColor: `${t.accent}55` }}>
                    <s.Icon className="w-5 h-5" style={{ color: t.accent }} />
                  </div>
                  <h3 className="mt-4 text-white font-semibold text-lg font-[Outfit]">{s.title}</h3>
                  <p className="mt-2 text-sm text-slate-300">{s.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium" style={{ color: t.accent }}>
                    {s.cta} <ChevronRight className="w-4 h-4 transition group-hover:translate-x-1" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Motivation card */}
      <section className="pb-16 lg:pb-20">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div
            className="relative rounded-3xl overflow-hidden border border-white/10 p-10 lg:p-14 text-center"
            style={{
              background:
                "radial-gradient(600px 400px at 20% 20%, rgba(16, 185, 129, 0.35), transparent 60%)," +
                "radial-gradient(600px 500px at 85% 85%, rgba(249, 115, 22, 0.3), transparent 60%)," +
                "linear-gradient(160deg, #0F172A 0%, #1E293B 50%, #0B1120 100%)",
            }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-emerald-200 font-medium tracking-wide">
              <Sparkles className="w-3.5 h-3.5" /> Phrase du jour
            </div>
            <motion.p
              key={quote}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="mt-6 text-white font-[Outfit] font-bold text-2xl sm:text-3xl lg:text-4xl max-w-3xl mx-auto leading-tight"
              data-testid="home-quote"
            >
              « {quote} »
            </motion.p>
            <button
              onClick={refreshQuote}
              disabled={loading}
              data-testid="home-quote-refresh"
              className="mt-8 inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white px-5 py-2.5 rounded-full text-sm"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              Une autre phrase
            </button>
          </div>
        </div>
      </section>

      <CommunitySection />
      <PublicStatsWidget />
    </>
  );
}
